// Visual values pulled from Figma node 7106:3047 (AI Import - Program
// Selection) via the Figma MCP. Card width 470px, 16px radius, 1px #EEE
// border + Shadow/Theme/100. Selected program option uses a #444 border;
// unselected uses #EEE on a #FCFCFC bg.
import React, { useCallback, useEffect } from 'react';
import styled from '@emotion/styled';
import { useDispatch, useSelector } from 'react-redux';
import HeaderText from '../../../shared/components/HeaderText/HeaderText';
import Text from '../../../shared/components/Text/Text';
import { setSelectedProgram, setStep, extractProgram } from '../ducks';
import { IMPORT_STEPS } from '../ducks/actionTypes';

const Card = styled('div')`
  width: 100%;
  max-width: 470px;
  background: #FFFFFF;
  border: 1px solid #EEEEEE;
  border-radius: 16px;
  box-shadow:
    0 0 0.5px 0 rgba(48, 49, 51, 0.05),
    0 16px 12px 0 rgba(48, 49, 51, 0.10);
  padding: 32px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  box-sizing: border-box;

  @media (max-width: 600px) {
    padding: 20px;
    border-radius: 12px;
  }
`;

const TitleBlock = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.5;
  width: 100%;
`;

const Title = styled(HeaderText)`
  font-size: 24px;
  font-weight: 900;
  margin: 0;
  letter-spacing: 0;
  line-height: 1.5;
  width: 100%;

  @media (max-width: 600px) {
    font-size: 20px;
  }
`;

const Subtitle = styled(Text)`
  font-size: 16px;
  font-weight: 400;
  color: #9E9E9E;
  margin: 0;
  line-height: 1.5;
  width: 100%;
`;

// Each program card: bg #FCFCFC, 8px padding, 8px radius, 4px gap between
// header + details block. Selected state swaps the border from #EEE → #444.
const ProgramOption = styled(({ isSelected, ...rest }) => <div {...rest} />)`
  background: #FCFCFC;
  border: 1px solid ${(props) => (props.isSelected ? '#444444' : '#EEEEEE')};
  border-radius: 8px;
  padding: 8px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  width: 100%;
  cursor: pointer;
  transition: border-color 0.18s ease;
  box-sizing: border-box;

  &:hover {
    border-color: ${(props) => (props.isSelected ? '#444444' : '#C9C9D4')};
  }
`;

// Header row with the program name. The leading slot is a 16px radio glyph
// (filled #444 when selected, hollow #EEE otherwise) per Figma node 7106:3047.
const ProgramHeader = styled('div')`
  display: flex;
  align-items: center;
  gap: 4px;
`;

// 16px radio circle. Selected state fills the inner dot (8px) and bumps the
// ring to #444 — matches the filled solid circle in Figma. Hollow state
// uses the #EEE token consistent with other input borders.
const RadioGlyph = styled(({ isSelected, ...rest }) => <span {...rest} />)`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid ${(props) => (props.isSelected ? '#444444' : '#E0E0E0')};
  background: #FFFFFF;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: border-color 0.18s ease;

  &::after {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${(props) => (props.isSelected ? '#444444' : 'transparent')};
    transition: background 0.18s ease;
  }
`;

const ProgramName = styled(Text)`
  font-size: 16px;
  font-weight: 700;
  margin: 0;
  line-height: 1.5;
`;

// Details block sits indented 20px so it aligns with the program name,
// past where the (optional) icon would render.
const ProgramDetails = styled('div')`
  padding: 0 20px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  box-sizing: border-box;
`;

const DetailRow = styled('div')`
  display: flex;
  align-items: center;
  gap: 4px;
  line-height: 1.5;
`;

const DetailLabel = styled('span')`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 400;
  color: #9E9E9E;
  line-height: 1.5;
`;

const DetailValue = styled('span')`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 400;
  color: #444444;
  line-height: 1.5;
  white-space: nowrap;
`;

// Sheet "tag" chip — bg #E0E0E0, 4px padding (2 vertical), 4px radius,
// SemiBold 12px #9E9E9E text.
const SheetTag = styled('span')`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #E0E0E0;
  border-radius: 4px;
  padding: 2px 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #9E9E9E;
  line-height: 1.5;
  white-space: nowrap;
`;

const HelpText = styled(Text)`
  font-size: 12px;
  font-weight: 400;
  color: #9E9E9E;
  margin: 0;
  line-height: 1.5;
  width: 100%;
`;

const ErrorText = styled(Text)`
  font-size: 12px;
  font-weight: 700;
  color: #C43D32;
  background: rgba(196, 61, 50, 0.08);
  border: 1px solid rgba(196, 61, 50, 0.2);
  border-radius: 8px;
  padding: 12px 16px;
  margin: 0;
  line-height: 1.5;
  width: 100%;
  box-sizing: border-box;
`;

// Continue button — Figma node 7106:3109 enabled state: bg #444, white
// text, Nunito Sans Bold 13px with 0.39px tracking, 4px radius, 6px
// vertical padding / 16px horizontal. Disabled mirrors the SCAN button
// disabled tokens (#E0E0E0 + #757575).
const ContinueButton = styled('button')`
  width: 100%;
  background: ${(props) => (props.disabled ? '#E0E0E0' : '#444444')};
  color: ${(props) => (props.disabled ? '#757575' : '#FFFFFF')};
  border: none;
  border-radius: 4px;
  padding: 6px 16px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.39px;
  line-height: 1.5;
  text-transform: uppercase;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  transition: background 0.18s ease, transform 0.18s ease, box-shadow 0.18s ease;

  &:hover:not(:disabled) {
    background: #2a2a2a;
    box-shadow: 0 4px 16px rgba(48, 49, 51, 0.25);
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }
`;

const StepProgramDetector = () => {
  const dispatch = useDispatch();
  const programs = useSelector((state) => state.aiProgramImport.programs);
  const selectedProgram = useSelector((state) => state.aiProgramImport.selectedProgram);
  const sessionId = useSelector((state) => state.aiProgramImport.sessionId);
  const extractError = useSelector((state) => state.aiProgramImport.extractError);

  const handleContinue = useCallback(() => {
    if (!selectedProgram) return;
    dispatch(setStep(IMPORT_STEPS.PROCESSING));
    dispatch(extractProgram(sessionId, selectedProgram));
  }, [dispatch, selectedProgram, sessionId]);

  const handleSelectProgram = useCallback((name) => {
    dispatch(setSelectedProgram(name));
  }, [dispatch]);

  // Auto-select when there's only one detected program — no reason to make
  // the coach click a single option. They can still review the duration /
  // sheet details before extracting.
  useEffect(() => {
    if (programs.length === 1 && !selectedProgram) {
      dispatch(setSelectedProgram(programs[0].name));
    }
  }, [dispatch, programs, selectedProgram]);

  return (
    <Card>
      <TitleBlock>
        <Title>Programs detected</Title>
        <Subtitle>Select the program you want to import.</Subtitle>
      </TitleBlock>

      {programs.map((program) => {
        const sheets = program.location?.sheets || [];
        const isSelected = selectedProgram === program.name;
        return (
          <ProgramOption
            key={program.name}
            isSelected={isSelected}
            onClick={() => handleSelectProgram(program.name)}
          >
            <ProgramHeader>
              <RadioGlyph isSelected={isSelected} aria-hidden='true' />
              <ProgramName>{program.name}</ProgramName>
            </ProgramHeader>
            <ProgramDetails>
              <DetailRow>
                <DetailLabel>Duration</DetailLabel>
                <DetailValue>
                  {program.weeks}
                  {' '}
                  week
                  {program.weeks !== 1 ? 's' : ''}
                  ,
                  {' '}
                  {program.daysPerWeek}
                  {' '}
                  day
                  {program.daysPerWeek !== 1 ? 's' : ''}
                  /week
                </DetailValue>
              </DetailRow>
              {sheets.length > 0 && (
                <DetailRow>
                  <DetailLabel>Found in</DetailLabel>
                  {sheets.map((sheet) => (
                    <SheetTag key={sheet}>{sheet}</SheetTag>
                  ))}
                </DetailRow>
              )}
            </ProgramDetails>
          </ProgramOption>
        );
      })}

      <HelpText>
        Not seeing the right program? Make sure your spreadsheet is formatted with one program
        per tab and try again.
      </HelpText>

      {extractError && <ErrorText>{extractError}</ErrorText>}

      <ContinueButton disabled={!selectedProgram} onClick={handleContinue}>
        Continue
      </ContinueButton>
    </Card>
  );
};

export default StepProgramDetector;
